// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Type-safe permission paths registry
 * Add new permission paths here to make them available throughout the application
 */
export type PermissionPath = 'time.rate';

/**
 * Permission action types matching the backend authorization system
 */
export type PermissionAction = 'create' | 'read' | 'update' | 'delete' | '*';

/**
 * Permission result types returned by policy evaluation
 */
export enum Permission {
  /** The permission was not found in the manifest */
  PermissionNotFound = 'PermissionNotFound',
  /** The permission was explicitly set to true on an exact path */
  PermissionExplicitTrue = 'PermissionExplicitTrue',
  /** The permission was explicitly set to false on an exact path */
  PermissionExplicitFalse = 'PermissionExplicitFalse',
  /** The permission was implicitly inherited as true from a parent path */
  PermissionImplicitTrue = 'PermissionImplicitTrue',
  /** The permission was implicitly inherited as false from a parent path */
  PermissionImplicitFalse = 'PermissionImplicitFalse',
}

/**
 * Validates if a string is a valid permission action
 */
export function isValidPermissionAction(
  action: string
): action is PermissionAction {
  return ['create', 'read', 'update', 'delete', '*'].includes(action);
}

/**
 * Permission action bit flags for efficient storage and operations
 */
export enum PermissionActionBit {
  CREATE = 1, // 1 << 0
  READ = 2, // 1 << 1
  UPDATE = 4, // 1 << 2
  DELETE = 8, // 1 << 3
}

/**
 * Internal permission value storage
 */
export interface PermissionValue {
  /** Bitset of actions that are configured */
  actions: number;
  /** Bitset of actions that are allowed */
  actionsAllowed: number;
}
